import JSZip from "jszip";
import { db, storage, auth } from "@/lib/firebase";
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  doc, 
  writeBatch 
} from "firebase/firestore";
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from "firebase/storage";

export interface ImportResult {
  deckId: string;
  cardCount: number;
}

export const importService = {
  async importDeckFromZip(
    file: File, 
    onProgress?: (current: number, total: number, status: string) => void
  ): Promise<ImportResult> {
    const user = auth.currentUser;
    if (!user) throw new Error("Unauthorized");

    if (onProgress) onProgress(0, 100, "Đang tải tệp ZIP...");
    const zip = await JSZip.loadAsync(file);
    console.log("[Import] ZIP Contents:", Object.keys(zip.files));
    console.log("ZIP Loaded. Files found:", Object.keys(zip.files));
    
    // 1. Find metadata.json
    if (onProgress) onProgress(5, 100, "Đang đọc metadata.json...");
    let metadataFile = zip.file("metadata.json");
    let basePath = "";

    if (!metadataFile) {
      const possiblePath = Object.keys(zip.files).find(path => path.endsWith("metadata.json"));
      if (possiblePath) {
        metadataFile = zip.file(possiblePath);
        basePath = possiblePath.replace("metadata.json", "");
      }
    }

    if (!metadataFile) {
      throw new Error("Không tìm thấy metadata.json trong tệp ZIP");
    }
    
    const metadataContent = await metadataFile.async("string");
    const metadata = JSON.parse(metadataContent);
    const { title, description, cards } = metadata;

    if (!title || !cards || !Array.isArray(cards)) {
      throw new Error("Cấu trúc metadata.json không hợp lệ");
    }

    // 2. Create Deck
    if (onProgress) onProgress(10, 100, "Đang khởi tạo bộ thẻ...");
    const deckRef = await addDoc(collection(db, "decks"), {
      userId: user.uid,
      title,
      description: description || "",
      cardCount: cards.length,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const batch = writeBatch(db);
    let cardsProcessed = 0;

    // 3. Process Cards (Parallel)
    if (onProgress) onProgress(15, 100, `Đang xử lý ${cards.length} thẻ...`);
    
    let completedCards = 0;
    const processCard = async (cardData: any, index: number) => {
      const { front, back } = cardData;
      
      const processSide = async (sideData: any, sideName: string) => {
        if (!sideData) return null;
        
        const side: any = {
          text: sideData.text || "",
          lang: sideData.lang || (sideName === "front" ? "en-US" : "vi-VN")
        };

        const imageFile = sideData.imageFile || sideData.image;
        if (imageFile) {
          const cleanImageFile = imageFile.replace(/^assets\//, "");
          const imagePath = `${basePath}assets/${cleanImageFile}`;
          const winImagePath = imagePath.replace(/\//g, "\\");
          const imageBlobFile = zip.file(imagePath) || zip.file(winImagePath);
          
          if (imageBlobFile) {
            const blob = await imageBlobFile.async("blob");
            const storageRef = ref(storage, `decks/${deckRef.id}/images/${sideName}_${Date.now()}_${cleanImageFile}`);
            await uploadBytes(storageRef, blob);
            side.image = await getDownloadURL(storageRef);
          }
        }

        const audioFile = sideData.audioFile || sideData.audio;
        if (audioFile) {
          const cleanAudioFile = audioFile.replace(/^assets\//, "");
          const audioPath = `${basePath}assets/${cleanAudioFile}`;
          const winAudioPath = audioPath.replace(/\//g, "\\");
          const audioBlobFile = zip.file(audioPath) || zip.file(winAudioPath);
          
          if (audioBlobFile) {
            const blob = await audioBlobFile.async("blob");
            const storageRef = ref(storage, `decks/${deckRef.id}/audio/${sideName}_${Date.now()}_${cleanAudioFile}`);
            await uploadBytes(storageRef, blob);
            side.audio = await getDownloadURL(storageRef);
          }
        }
        return side;
      };

      const [processedFront, processedBack] = await Promise.all([
        processSide(front, "front"),
        processSide(back, "back")
      ]);

      const cardRef = doc(collection(db, "cards"));
      batch.set(cardRef, {
        deckId: deckRef.id,
        userId: user.uid,
        front: processedFront,
        back: processedBack,
        tags: cardData.tags || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      completedCards++;
      const currentProgress = Math.floor(15 + (completedCards / cards.length) * 80);
      if (onProgress) onProgress(currentProgress, cards.length, `Đang đồng bộ: ${completedCards}/${cards.length}`);
    };

    await Promise.all(cards.map((c, i) => processCard(c, i)));

    // 4. Commit Batch
    if (onProgress) onProgress(95, 100, "Đang lưu dữ liệu...");
    await batch.commit();
    if (onProgress) onProgress(100, 100, "Hoàn tất!");

    return {
      deckId: deckRef.id,
      cardCount: cardsProcessed
    };
  }
};
